import { useState, useEffect } from "react";
import { useHistory } from "react-router";
import { Box, Typography, CircularProgress, useTheme, useMediaQuery, Button } from "@material-ui/core";
import { createPaymentIntent } from "../../../api/Quote/QuoteApi";
import { PaymentIntentResponse } from "../../../api/Quote/Payment";
import { toast } from "react-toastify";
import useStyles from "./PaymentForm.styles";
import PaymentCardDetails from "./PaymentCardDetails";
import PaymentAmountSelection from "./PaymentAmountSelection";
import { useSelector } from "react-redux";
import { RootState } from "../../../app/rootReducer";

type Step = "select" | "pay";

interface PaymentFormProps {
  onPaymentSuccess: (amount: number) => void;
}

export const PaymentForm = ({ onPaymentSuccess }: PaymentFormProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const [step, setStep] = useState<Step>("select");
  const [paymentType, setPaymentType] = useState<"full" | "partial">("full");
  const [amount, setAmount] = useState<number>(0);
  const [customAmount, setCustomAmount] = useState<number>(0);
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntentResponse | null>(null);
  const history = useHistory();

  const { quote, balance: balanceObj, balanceError } = useSelector((state: RootState) => state.quote);

  // If balanceError, allow only partial payment selection between min and max (20k)
  const isBalanceError = Boolean(balanceError);
  const maxPartial = isBalanceError ? 20000 : Math.min(balanceObj?.balance || 0, 20000);
  const defaultMinimum = 50;
  const defaultUnknownBalance = 1000;
  const balance = isBalanceError ? 0 : balanceObj?.balance || 0;

  // Set amount to default minimum if balanceError and step is 'select'
  useEffect(() => {
    if (isBalanceError && step === "select" && amount === 0) {
      setAmount(defaultUnknownBalance);
      setCustomAmount(defaultUnknownBalance);
      setPaymentType("partial");
    } else if (
      step === "select" &&
      paymentType === "partial" &&
      balance > 0 &&
      customAmount === 0 &&
      amount === Math.min(defaultMinimum, balance || defaultMinimum)
    ) {
      let defaultPartial = 100;
      if (balance > 5000) defaultPartial = 5000;
      else if (balance > 3000) defaultPartial = 3000;
      else if (balance > 1000) defaultPartial = 1000;
      else if (balance > 500) defaultPartial = 500;
      setCustomAmount(defaultPartial);
      setAmount(defaultPartial);
    } else if (step === "select" && paymentType === "full" && balance > 0) {
      setAmount(balance);
    }
  }, [balance, step, paymentType, customAmount, defaultMinimum, amount, isBalanceError]);

  // Set amount based on payment type
  const handlePaymentTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isBalanceError) return;
    const value = e.target.value as "full" | "partial";
    setPaymentType(value);
    if (value === "full") {
      setAmount(balance);
    } else {
      setAmount(customAmount > 0 ? customAmount : Math.min(defaultMinimum, balance || defaultMinimum));
    }
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    setCustomAmount(val);
    setAmount(val);
  };

  if (!quote || (balanceObj === null && !balanceError))
    return (
      <Box className={classes.root} display="flex" alignItems="center" justifyContent="center" minHeight={360}>
        <CircularProgress />
      </Box>
    );

  const handleReturnToQuote = () => {
    history.push(`/quote/${quote.id}`);
  };

  // Show paid up message if balance is 0 and no balanceError
  if (balanceObj && balance <= 0 && !isBalanceError) {
    return (
      <Box
        className={classes.root}
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight={360}
        flexDirection={"column"}
        textAlign="center"
      >
        <Typography variant="h6" color="primary" gutterBottom>
          🎉 It looks like you're all paid up!
        </Typography>
        <Button variant="contained" color="secondary" onClick={handleReturnToQuote}>
          Return to Quote
        </Button>
      </Box>
    );
  }

  // Create payment intent and move to pay step
  const handleAmountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const intent = await createPaymentIntent(quote.id, amount);
      setPaymentIntent(intent);
      setStep("pay");
    } catch (err) {
      toast.error("Failed to create payment intent. Please try again.");
    }
  };

  // Payment success handler
  const handlePaymentSuccess = () => {
    onPaymentSuccess(amount);
  };

  return (
    <Box className={`${classes.paymentPageSection} ${classes.paymentSection}`}>
      {!isMdDown && (
        <Box className={classes.stripeLogo}>
          <img src="images/stripe.png" alt="stripe" width="100%" />
        </Box>
      )}
      <Typography variant="h1" gutterBottom>
        Make a Payment
      </Typography>
      {isMdDown && (
        <Box className={classes.stripeLogo}>
          <img src="images/stripe.png" alt="stripe" width="100%" />
        </Box>
      )}
      {step === "select" && (
        <PaymentAmountSelection
          balance={balance}
          maxPartial={maxPartial}
          paymentType={paymentType}
          amount={amount}
          customAmount={customAmount}
          defaultMinimum={defaultMinimum}
          isLoading={false}
          onPaymentTypeChange={handlePaymentTypeChange}
          onCustomAmountChange={handleCustomAmountChange}
          onSubmit={handleAmountSubmit}
          disablePaymentType={isBalanceError}
        />
      )}
      {step === "pay" && paymentIntent && (
        <PaymentCardDetails
          amount={amount}
          clientSecret={paymentIntent.clientSecret}
          onSuccess={handlePaymentSuccess}
          onBack={() => setStep("select")}
        />
      )}
    </Box>
  );
};
